import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/hooks/useAuth";
import PageTransition from "@/components/PageTransition";
import LoadingFallback from "@/components/LoadingFallback";

// Eagerly load the landing page for fastest FCP
import Index from "./pages/Index";

// Lazy-load all other routes
const About = lazy(() => import("./pages/About"));
const WasteCrisis = lazy(() => import("./pages/WasteCrisis"));
const Legal = lazy(() => import("./pages/Legal"));
const Contact = lazy(() => import("./pages/Contact"));
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const WishlistPage = lazy(() => import("./pages/WishlistPage"));
const SharedListPage = lazy(() => import("./pages/SharedListPage"));
const EventsPage = lazy(() => import("./pages/EventsPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const GiverDashboard = lazy(() => import("./pages/GiverDashboard"));
const GratitudePage = lazy(() => import("./pages/GratitudePage"));

const SetupCelebration = lazy(() => import("./pages/SetupCelebration"));
const AdminAnalytics = lazy(() => import("./pages/AdminAnalytics"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const CashFundPage = lazy(() => import("./pages/CashFundPage"));
const CommunityPage = lazy(() => import("./pages/CommunityPage"));
const NotFound = lazy(() => import("./pages/NotFound"));
const VirtualCardPage = lazy(() => import("./pages/VirtualCardPage"));
const Unsubscribe = lazy(() => import("./pages/Unsubscribe").then(m => ({ default: m.Unsubscribe })));

const queryClient = new QueryClient();

function AnimatedRoutes() {
  const location = useLocation();

  return (
    <AnimatePresence mode="wait">
      <Suspense fallback={<LoadingFallback />}>
        <Routes location={location} key={location.pathname}>
          <Route path="/" element={<PageTransition><Index /></PageTransition>} />
          <Route path="/about" element={<PageTransition><About /></PageTransition>} />
          <Route path="/waste-crisis" element={<PageTransition><WasteCrisis /></PageTransition>} />
          <Route path="/legal" element={<PageTransition><Legal /></PageTransition>} />
          <Route path="/contact" element={<PageTransition><Contact /></PageTransition>} />
          <Route path="/auth" element={<PageTransition><Auth /></PageTransition>} />
          <Route path="/forgot-password" element={<PageTransition><ForgotPassword /></PageTransition>} />
          <Route path="/reset-password" element={<PageTransition><ResetPassword /></PageTransition>} />
          <Route path="/wishlist" element={<PageTransition><WishlistPage /></PageTransition>} />
          <Route path="/shared/:slug" element={<PageTransition><SharedListPage /></PageTransition>} />
          <Route path="/events" element={<PageTransition><EventsPage /></PageTransition>} />
          <Route path="/dashboard" element={<PageTransition><DashboardPage /></PageTransition>} />
          <Route path="/manual/:id" element={<PageTransition><GiverDashboard /></PageTransition>} />
          <Route path="/gratitude/:claimId" element={<PageTransition><GratitudePage /></PageTransition>} />
          <Route path="/dashboard/unboxing" element={<Navigate to="/dashboard" replace />} />
          <Route path="/celebrate" element={<PageTransition><SetupCelebration /></PageTransition>} />
          <Route path="/admin/analytics" element={<PageTransition><AdminAnalytics /></PageTransition>} />
          <Route path="/blog" element={<PageTransition><BlogPage /></PageTransition>} />
          <Route path="/blog/:slug" element={<PageTransition><BlogPostPage /></PageTransition>} />
          <Route path="/fund/:slug" element={<PageTransition><CashFundPage /></PageTransition>} />
          <Route path="/community" element={<PageTransition><CommunityPage /></PageTransition>} />
          <Route path="/card/:token" element={<PageTransition><VirtualCardPage /></PageTransition>} />
          <Route path="/unsubscribe" element={<PageTransition><Unsubscribe /></PageTransition>} />
          {/* Redirects for old routes */}
          <Route path="/profile" element={<Navigate to="/dashboard" replace />} />
          <Route path="/profile/me" element={<Navigate to="/dashboard" replace />} />
          <Route path="/soul-sync" element={<Navigate to="/dashboard" replace />} />
          <Route path="/spark-sync" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<PageTransition><NotFound /></PageTransition>} />
        </Routes>
      </Suspense>
    </AnimatePresence>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AnimatedRoutes />
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
